import { api as $axios } from '@/helpers/interceptors'
import { VIP_CAMPAIGNS, VIP_ORDERS } from '@/constants/path'
import snakeCaseKeys from 'snakecase-keys'
import { meta } from '@/config/pagination'

const state = () => ({
  convertRightsReport: null,
  convertRightsMessage: '',
  isLoadingConvertRights: false,
  isErrorConvertRights: false,
  orders: [],
  ordersCount: 0,
  ordersMeta: meta,
  isLoadingOrders: false,
  summary: {
    grantedPending: null,
    notGrantedPending: null,
    converted: null,
  },
  isLoadingManualConvert: false,
})

// getters
const getters = {}

// actions
const actions = {
  async importConvertRights(
    { commit },
    { mainCampaignId, file, action = 'grant', dryRun = true },
  ) {
    commit('SET_STATE', {
      key: 'isLoadingConvertRights',
      value: true,
    })
    commit('SET_STATE', { key: 'isErrorConvertRights', value: false })
    commit('SET_STATE', { key: 'convertRightsMessage', value: '' })
    commit('SET_STATE', { key: 'convertRightsReport', value: null })
    try {
      const form = new FormData()
      form.append('file', file)
      form.append('action', action)
      form.append('dry_run', dryRun ? '1' : '0')
      const res = await $axios(
        `${VIP_CAMPAIGNS}/${mainCampaignId}/convert-rights`,
        'post',
        form,
        { timeout: 120000 },
      )
      commit('SET_STATE', {
        key: 'convertRightsReport',
        value: res?.data?.data,
      })
      commit('SET_STATE', {
        key: 'convertRightsMessage',
        value: res?.data?.message,
      })
      commit('SET_STATE', {
        key: 'isLoadingConvertRights',
        value: false,
      })
      return res?.data?.data
    } catch (error) {
      // 422 with file errors carries a report in `data`; other errors have `data: null`
      commit('SET_STATE', {
        key: 'convertRightsReport',
        value: error?.response?.data?.data ?? null,
      })
      commit('SET_STATE', {
        key: 'convertRightsMessage',
        value: error?.response?.data?.message ?? '',
      })
      commit('SET_STATE', {
        key: 'isErrorConvertRights',
        value: true,
      })
      commit('SET_STATE', {
        key: 'isLoadingConvertRights',
        value: false,
      })
    }
  },

  async getVipOrders({ commit }, { mainCampaignId, ...params }) {
    commit('SET_STATE', { key: 'isLoadingOrders', value: true })
    try {
      const res = await $axios(
        `${VIP_CAMPAIGNS}/${mainCampaignId}/orders`,
        'get',
        snakeCaseKeys({ params }),
      )
      commit('SET_STATE', { key: 'orders', value: res?.data?.data })
      // ใช้ count เป็นจำนวนทั้งหมด (meta.total_item* ชื่อชวนสับสน)
      commit('SET_STATE', {
        key: 'ordersCount',
        value: res?.data?.count ?? 0,
      })
      commit('SET_STATE', {
        key: 'ordersMeta',
        value: res?.data?.meta,
      })
    } catch (error) {
      commit('SET_STATE', { key: 'orders', value: [] })
      commit('SET_STATE', { key: 'ordersCount', value: 0 })
    }
    commit('SET_STATE', { key: 'isLoadingOrders', value: false })
  },

  // ตัวเลขสรุป: เรียก per_page=1 แล้วอ่าน count
  async getVipOrderSummary({ commit }, mainCampaignId) {
    const countOf = async (params) => {
      const res = await $axios(
        `${VIP_CAMPAIGNS}/${mainCampaignId}/orders`,
        'get',
        snakeCaseKeys({ params: { ...params, page: 1, perPage: 1 } }),
      )
      return res?.data?.count ?? 0
    }
    try {
      const [grantedPending, notGrantedPending, converted] =
        await Promise.all([
          countOf({ status: 'vip_pending', right: 'granted' }),
          countOf({ status: 'vip_pending', right: 'not_granted' }),
          countOf({ status: 'vip_converted' }),
        ])
      commit('SET_STATE', {
        key: 'summary',
        value: { grantedPending, notGrantedPending, converted },
      })
    } catch (error) {
      commit('SET_STATE', {
        key: 'summary',
        value: {
          grantedPending: null,
          notGrantedPending: null,
          converted: null,
        },
      })
    }
  },

  // 422 ไม่ได้สิทธิ์: data.status === 'not_granted' (force ข้ามสิทธิ์ไม่ได้)
  async manualConvertVipOrder({ commit }, { uuid, force = false }) {
    commit('SET_STATE', {
      key: 'isLoadingManualConvert',
      value: true,
    })
    try {
      const res = await $axios(
        `${VIP_ORDERS}/${uuid}/manual-convert`,
        'post',
        { force },
      )
      commit('SET_STATE', {
        key: 'isLoadingManualConvert',
        value: false,
      })
      return { success: true, data: res?.data?.data }
    } catch (error) {
      commit('SET_STATE', {
        key: 'isLoadingManualConvert',
        value: false,
      })
      return {
        success: false,
        data: error?.response?.data?.data ?? null,
        message: error?.response?.data?.message ?? '',
      }
    }
  },
}

// mutations
const mutations = {
  SET_STATE(state, ops) {
    state[ops.key] = ops.value
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
